import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  AlertTitle,
  LinearProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  PlayCircleOutlined as PlayCircleIcon,
} from '@mui/icons-material';
import { useDueWords, useReviewVocabularyWord } from '../../hooks/useVocabulary';
import { useTrackListeningTime } from '../../hooks/useListeningTime';
import { useNavigate, useSearchParams } from 'react-router';
import { ROUTES } from '../../config/constants';
import toast from 'react-hot-toast';
import { LanguageBadge } from '../../components/vocabulary/LanguageBadge';
import { WordAudioButton } from '../../components/vocabulary/WordAudioButton';
import { ReviewIntervalChart } from '../../components/vocabulary/ReviewIntervalChart';
import { YouTubeSegmentModal } from '../../components/youtube/YouTubeSegmentModal';
import { VocabularySessionComplete } from './VocabularySessionComplete';

export const VocabularyReview = () => {
  const { t } = useTranslation('vocabulary');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedLanguage = searchParams.get('language') || undefined;
  
  const { data: allDueWords, isLoading, error } = useDueWords(selectedLanguage);
  const { mutate: reviewWord, isPending: isReviewing } = useReviewVocabularyWord();
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [reviewedWords, setReviewedWords] = useState<number[]>([]);
  const [easyCount, setEasyCount] = useState(0);
  const [needsWorkCount, setNeedsWorkCount] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [skippedWordIds, setSkippedWordIds] = useState<number[]>([]);
  const [showSkippedAlert, setShowSkippedAlert] = useState(true);
  const [playingSegmentId, setPlayingSegmentId] = useState<number | null>(null);
  const [audioInstance, setAudioInstance] = useState<HTMLAudioElement | null>(null);
  const [modalSegment, setModalSegment] = useState<{
    youtubeVideoId: string;
    startTime: number;
    endTime: number;
    videoTitle: string;
  } | null>(null);
  const isModalOpen = modalSegment !== null;
  const { mutate: trackListeningTime } = useTrackListeningTime();
  const playStartRef = useRef<number | null>(null);
  const wordAudioButtonRef = useRef<HTMLButtonElement | null>(null);

  // Filter out words without translations and track skipped words
  const { dueWords, skippedWords } = useMemo(() => {
    if (!allDueWords) return { dueWords: [], skippedWords: [] };
    
    const valid: typeof allDueWords = [];
    const skipped: typeof allDueWords = [];
    
    allDueWords.forEach(word => {
      // Skip words that have already been reviewed OR skipped in this session
      if (reviewedWords.includes(word.id) || skippedWordIds.includes(word.id)) {
        return;
      }
      
      if (word.translation && word.translation.trim().length > 0) {
        valid.push(word);
      } else {
        skipped.push(word);
      }
    });
    
    return { dueWords: valid, skippedWords: skipped };
  }, [allDueWords, reviewedWords, skippedWordIds]);

  const currentWord = dueWords?.[currentIndex];

  const daysSinceReviewLabel = useMemo(() => {
    if (!currentWord) return '';
    if (!currentWord.lastReviewedAt) return t('reviewPage.neverReviewed');
    const last = new Date(currentWord.lastReviewedAt);
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.max(0, Math.round((startOfDay(now) - startOfDay(last)) / 86400000));
    return days === 0
      ? t('reviewPage.lastReviewedToday')
      : t('reviewPage.daysSinceReview', { count: days });
  }, [currentWord, t]);
  
  // Calculate total dynamically: words already reviewed + words still due
  // This accounts for backend refetches that might add/remove words
  const totalWords = reviewedWords.length + (dueWords?.length || 0) + skippedWordIds.length;
  const reviewedCount = reviewedWords.length;
  const progress = totalWords > 0 ? (reviewedCount / totalWords) * 100 : 0;

  // Show toast notification once when words are skipped
  useEffect(() => {
    if (!isLoading && skippedWords.length > 0) {
      toast.error(
        t('reviewPage.skippedToast', { count: skippedWords.length }),
        { duration: 5000, id: 'vocabulary-skipped' } // Use id to prevent duplicates
      );
    }
  }, [isLoading, skippedWords.length, t]);

  useEffect(() => {
    // If no valid due words after filtering, redirect back to vocabulary list
    if (!isLoading && dueWords && dueWords.length === 0) {
      // If any words were reviewed, celebrate — even if some were auto-skipped (no translation).
      // Show the celebration first; the skipped-words info is secondary.
      if (reviewedWords.length > 0) {
        setSessionComplete(true);
        return;
      }
      // Only reach here when ZERO words were reviewed — all were auto-skipped (no translations).
      if (skippedWords.length > 0) {
        toast.error(t('reviewPage.allSkipped'), {
          duration: 6000,
        });
        return;
      }
      // Small delay to ensure toast shows before navigation
      // If no more unreviewed words, check if we have skipped words to re-add
      if (dueWords.length === 0 && skippedWordIds.length > 0) {
        // Re-add skipped words back to the queue for another chance
        setSkippedWordIds([]);
        toast(t('reviewPage.retryingSkipped', { count: skippedWordIds.length }), {
          icon: '🔄',
          duration: 4000,
        });
        return;
      }
      
      // If truly no more words left, go back to vocabulary list
      const timer = setTimeout(() => {
        navigate(ROUTES.VOCABULARY);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [dueWords, isLoading, navigate, skippedWordIds, reviewedWords.length]);

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  const handleRate = (rating: 'again' | 'hard' | 'medium' | 'easy') => {
    if (!currentWord) return;

    // Stop any playing audio before advancing to next card
    if (audioInstance) {
      audioInstance.pause();
      audioInstance.currentTime = 0;
      setPlayingSegmentId(null);
      setAudioInstance(null);
    }

    // Update local state IMMEDIATELY before mutation to prevent flashing
    const wordIdToReview = currentWord.id;
    setReviewedWords(prev => [...prev, wordIdToReview]);
    setCurrentIndex(0);
    setShowAnswer(false);

    // Track per-session rating breakdown
    if (rating === 'easy') {
      setEasyCount(prev => prev + 1);
    } else if (rating === 'again' || rating === 'hard') {
      setNeedsWorkCount(prev => prev + 1);
    }

    // Then submit the review in the background
    reviewWord({
      wordId: wordIdToReview,
      rating,
    });
  };

  const handleSkip = () => {
    if (!currentWord) return;
    
    // Stop any playing audio before skipping to next card
    if (audioInstance) {
      audioInstance.pause();
      audioInstance.currentTime = 0;
      setPlayingSegmentId(null);
      setAudioInstance(null);
    }
    
    // Add to skipped words (separate from reviewed words)
    setSkippedWordIds([...skippedWordIds, currentWord.id]);
    setCurrentIndex(0);
    setShowAnswer(false);
    
    // Show feedback
    toast(t('reviewPage.wordSkipped'), {
      icon: '⏭️',
      duration: 1500,
    });
  };

  // Keyboard shortcuts: Space/Enter reveals translation; 1-4 rate the card once
  // revealed; S skips; P plays word pronunciation. Disabled while the YouTube
  // context modal is open or while a review is in flight.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isModalOpen || isReviewing) return;

      const target = event.target as HTMLElement | null;
      const isTypingTarget = target && ['INPUT', 'TEXTAREA'].includes(target.tagName);
      if (isTypingTarget) return;

      if (event.key === 'p' || event.key === 'P') {
        event.preventDefault();
        wordAudioButtonRef.current?.click();
        return;
      }

      if (!showAnswer) {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          handleShowAnswer();
        }
        return;
      }

      switch (event.key) {
        case '1':
          event.preventDefault();
          handleRate('again');
          break;
        case '2':
          event.preventDefault();
          handleRate('hard');
          break;
        case '3':
          event.preventDefault();
          handleRate('medium');
          break;
        case '4':
          event.preventDefault();
          handleRate('easy');
          break;
        case 's':
        case 'S':
          event.preventDefault();
          handleSkip();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, isReviewing, showAnswer, currentWord]);

  const handlePlaySegment = (segmentId: number, audioUrl: string, startTime: number, endTime: number, audioOffset?: number, language?: string) => {
    const reportListeningTime = () => {
      if (playStartRef.current !== null) {
        const seconds = Math.floor((performance.now() - playStartRef.current) / 1000);
        playStartRef.current = null;
        if (seconds > 0 && language) {
          trackListeningTime({ seconds, language });
        }
      }
    };

    // If this segment is already playing, pause it
    if (playingSegmentId === segmentId && audioInstance) {
      audioInstance.pause();
      reportListeningTime();
      setPlayingSegmentId(null);
      setAudioInstance(null);
      return;
    }

    // Stop any currently playing audio
    if (audioInstance) {
      audioInstance.pause();
      reportListeningTime();
      audioInstance.currentTime = 0;
    }

    // Apply audio offset
    const offset = audioOffset || 0;
    const adjustedStart = startTime + offset;
    const adjustedEnd = endTime + offset;

    // Create audio element and play segment.
    // Seek only after metadata is loaded so currentTime is reliable cross-browser,
    // and always .catch() the play() promise to avoid unhandled rejection errors (NotSupportedError).
    const audio = new Audio(`${import.meta.env.VITE_API_BASE_URL}/audio/proxy?url=${encodeURIComponent(audioUrl)}`);

    const startPlayback = () => {
      audio.currentTime = adjustedStart;
      audio.play().catch((err) => {
        console.warn('[VocabularyReview] Audio playback failed:', err);
        playStartRef.current = null;
        setPlayingSegmentId(null);
        setAudioInstance(null);
      });
    };

    // HAVE_METADATA (readyState >= 1) means duration/currentTime are already available.
    if (audio.readyState >= 1) {
      startPlayback();
    } else {
      audio.addEventListener('loadedmetadata', startPlayback, { once: true });
    }

    playStartRef.current = performance.now();
    setPlayingSegmentId(segmentId);
    setAudioInstance(audio);

    // Stop playback when reaching segment end time
    const handleTimeUpdate = () => {
      if (audio.currentTime >= adjustedEnd) {
        audio.pause();
        reportListeningTime();
        setPlayingSegmentId(null);
        setAudioInstance(null);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
    audio.addEventListener('timeupdate', handleTimeUpdate);

    // Clear playing state when audio ends
    audio.addEventListener('ended', () => {
      reportListeningTime();
      setPlayingSegmentId(null);
      setAudioInstance(null);
    });

    // Clear playing state if audio errors
    audio.addEventListener('error', () => {
      playStartRef.current = null;
      setPlayingSegmentId(null);
      setAudioInstance(null);
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getNextReviewText = (rating: string, currentLevel: number) => {
    // Mirrors the backend SRS ladder in vocabularyService.go (srsIntervals / srsTargetLevels).
    const srsIntervals = [1, 2, 4, 7, 12, 21, 35, 60, 100, 165, 270, 365];
    const maxLevel = srsIntervals.length - 1;
    const clamp = (level: number) => Math.min(Math.max(level, 0), maxLevel);

    const cur = clamp(currentLevel);
    const again = 0;
    const hard = Math.max(cur, 1);
    const medium = Math.max(cur + 1, hard + 1);
    const easy = Math.max(cur + 2, medium + 1);

    const targetLevel: Record<string, number> = { again, hard, medium, easy };
    const days = srsIntervals[clamp(targetLevel[rating] ?? cur)];

    if (days === 1) return t('reviewPage.nextReview.tomorrow');
    if (days < 365) return t('reviewPage.nextReview.days', { count: days });
    return t('reviewPage.nextReview.oneYear');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('reviewPage.errorLoading')}
      </Alert>
    );
  }

  if (sessionComplete) {
    return (
      <VocabularySessionComplete
        totalReviewed={reviewedWords.length}
        easyCount={easyCount}
        needsWorkCount={needsWorkCount}
        onDone={() => navigate(ROUTES.VOCABULARY)}
      />
    );
  }

  // If no valid words to review, show a message
  if (!isLoading && (!dueWords || dueWords.length === 0)) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>{t('reviewPage.redirecting')}</Typography>
      </Box>
    );
  }

  if (!currentWord) {
    return null;
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(ROUTES.VOCABULARY)}
        >
          {t('reviewPage.backToVocabulary')}
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          {selectedLanguage && (
            <Chip 
              label={`${t('reviewPage.reviewing')}: ${selectedLanguage}`} 
              color="primary"
              size="small"
            />
          )}
          <Typography variant="h6">
            {reviewedCount} / {totalWords}
          </Typography>
          {skippedWordIds.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({skippedWordIds.length} {t('reviewPage.skipped')})
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Skipped Words Alert */}
      {skippedWords.length > 0 && showSkippedAlert && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon />}
          onClose={() => setShowSkippedAlert(false)}
          sx={{ mb: 3 }}
        >
          <AlertTitle>
            {t('reviewPage.skippedAlert.title', { count: skippedWords.length })}
          </AlertTitle>
          {t('reviewPage.skippedAlert.message', { count: skippedWords.length })}
          <strong> {skippedWords.map(w => w.word).join(', ')}</strong>
          <br />
          <Button
            size="small"
            variant="text"
            onClick={() => navigate(ROUTES.VOCABULARY)}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            {t('reviewPage.skippedAlert.goToList')}
          </Button>
        </Alert>
      )}

      {/* Progress Bar */}
      <LinearProgress variant="determinate" value={progress} sx={{ mb: 4 }} />

      {/* Flashcard */}
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Card sx={{ minHeight: 400 }}>
          <CardContent>
            {!showAnswer ? (
              /* Front of Card */
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <LanguageBadge language={currentWord.sourceLanguage} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="h2" component="div" gutterBottom sx={{ fontWeight: 'bold', mb: 0 }}>
                    {currentWord.word}
                  </Typography>
                  <WordAudioButton ref={wordAudioButtonRef} word={currentWord.word} language={currentWord.sourceLanguage} />
                  <Typography variant="caption" sx={{ opacity: 0.7, display: { xs: 'none', sm: 'inline' } }}>
                    (P)
                  </Typography>
                </Box>

                <Paper sx={{ p: 2, mt: 4, bgcolor: 'grey.100' }} variant="outlined">
                  <Typography variant="body1" color="text.secondary">
                    {currentWord.context}
                  </Typography>
                </Paper>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<VisibilityIcon />}
                  onClick={handleShowAnswer}
                  sx={{ mt: 4 }}
                >
                  {t('reviewPage.showTranslation')}
                  <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7, display: { xs: 'none', sm: 'inline' } }}>
                    (Space)
                  </Typography>
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  {daysSinceReviewLabel}
                </Typography>
              </Box>
            ) : (
              /* Back of Card */
              <Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: 3 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      <LanguageBadge language={currentWord.sourceLanguage} />
                      {currentWord.exposureTier && (() => {
                        const tierConfig: Record<string, { label: string; color: 'success' | 'primary' | 'warning' | 'default' | 'secondary' }> = {
                          strong:   { label: t('tier.strong'),   color: 'success' },
                          building: { label: t('tier.building'), color: 'primary' },
                          low:      { label: t('tier.low'),      color: 'warning' },
                          single:   { label: t('tier.single'),   color: 'default' },
                          phrase:   { label: t('tier.phrase'),   color: 'secondary' },
                        };
                        const c = tierConfig[currentWord.exposureTier];
                        return c ? <Chip label={c.label} size="small" color={c.color} variant="outlined" /> : null;
                      })()}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="h3" component="div" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 0 }}>
                        {currentWord.word}
                      </Typography>
                      <WordAudioButton ref={wordAudioButtonRef} word={currentWord.word} language={currentWord.sourceLanguage} />
                    </Box>

                    <Typography variant="h5" color="primary" gutterBottom sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                      {currentWord.translation}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: { md: '0 0 280px' }, width: { xs: '100%', md: 280 } }}>
                    <ReviewIntervalChart
                      createdAt={currentWord.createdAt}
                      history={currentWord.reviewHistory ?? []}
                      reviewCount={currentWord.reviewCount}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Image display */}
                {currentWord.imageUrl && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={currentWord.imageUrl}
                      alt={currentWord.word}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        borderRadius: 2,
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </Box>
                )}

                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }} variant="outlined">
                  <Typography variant="body1" color="text.secondary">
                    {currentWord.context}
                  </Typography>
                </Paper>

                {/* Segment Audio Links */}
                {currentWord.segments && currentWord.segments.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('reviewPage.hearInContext')}
                    </Typography>
                    <List dense>
                      {currentWord.segments.map((segment) => {
                        const isYouTubeSegment = !!segment.youtubeVideoId;
                        return (
                          <ListItem
                            key={segment.segmentId}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <ListItemText
                              sx={{ my: 0 }}
                              primary={
                                isYouTubeSegment
                                  ? (segment.youtubeTitle ?? t('reviewPage.youtubeSegment'))
                                  : `${segment.projectName} - ${segment.episodeName}`
                              }
                              secondary={`${formatTime(segment.startTime)} – ${formatTime(segment.endTime)}`}
                            />
                            <Box sx={{ flexShrink: 0, pt: 0.5 }}>
                              {isYouTubeSegment ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PlayCircleIcon />}
                                  onClick={() => {
                                    // Stop any playing podcast audio before opening the modal (Task 4.5)
                                    if (audioInstance) {
                                      audioInstance.pause();
                                      setAudioInstance(null);
                                      setPlayingSegmentId(null);
                                    }
                                    setModalSegment({
                                      youtubeVideoId: segment.youtubeVideoId!,
                                      startTime: segment.startTime,
                                      endTime: segment.endTime,
                                      videoTitle: segment.youtubeTitle ?? currentWord.word,
                                    });
                                  }}
                                >
                                  {t('reviewPage.playInContext')}
                                </Button>
                              ) : (
                                <IconButton
                                  onClick={() => handlePlaySegment(segment.segmentId, segment.audioUrl, segment.startTime, segment.endTime, segment.audioOffset, currentWord.sourceLanguage)}
                                  color={playingSegmentId === segment.segmentId ? 'primary' : 'default'}
                                >
                                  {playingSegmentId === segment.segmentId ? <PauseIcon /> : <PlayIcon />}
                                </IconButton>
                              )}
                            </Box>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Rating Buttons */}
                <Typography variant="subtitle2" gutterBottom textAlign="center">
                  {t('reviewPage.whenAgain')}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                  {([
                    { rating: 'again', color: 'error', key: '1' },
                    { rating: 'hard', color: 'warning', key: '2' },
                    { rating: 'medium', color: 'info', key: '3' },
                    { rating: 'easy', color: 'success', key: '4' },
                  ] as const).map(({ rating, color, key }) => (
                    <Button
                      key={rating}
                      data-testid={`rating-${rating}`}
                      variant="outlined"
                      color={color}
                      onClick={() => handleRate(rating)}
                      disabled={isReviewing}
                      sx={{ flex: 1 }}
                    >
                      {getNextReviewText(rating, currentWord.difficultyLevel)}
                      <Typography component="span" variant="caption" sx={{ ml: 0.75, opacity: 0.7, display: { xs: 'none', sm: 'inline' } }}>
                        ({key})
                      </Typography>
                    </Button>
                  ))}
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                  {daysSinceReviewLabel}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Skip Button — only shown after the user reveals the translation */}
        {showAnswer && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={handleSkip}
              disabled={isReviewing}
            >
              {t('reviewPage.skipWord')}
              <Typography component="span" variant="caption" sx={{ ml: 0.75, opacity: 0.7, display: { xs: 'none', sm: 'inline' } }}>
                (S)
              </Typography>
            </Button>
          </Box>
        )}
      </Box>

      {/* YouTube segment context modal — rendered at page level, no route change (AC 4) */}
      <YouTubeSegmentModal
        open={isModalOpen}
        onClose={() => setModalSegment(null)}
        youtubeVideoId={modalSegment?.youtubeVideoId ?? ''}
        startTime={modalSegment?.startTime ?? 0}
        endTime={modalSegment?.endTime ?? 0}
        videoTitle={modalSegment?.videoTitle ?? ''}
      />
    </Box>
  );
};
